import { useTranslation } from 'react-i18next';

import { ZoningType } from '../plot-map/plot-map';
import styles from './analysis-legend.module.css';

export interface AnalysisLegendProps {
  legend: { zone: number; color: string; value?: number; percent?: number }[];
  zoningType?: ZoningType;
}

function isYieldZoning(zoningType?: ZoningType): boolean {
  return zoningType === 'yield' || zoningType === 'yieldAnalysis';
}

function formatMetricValue(value: number): string {
  if (value === 0) {
    return '0';
  }
  if (Math.abs(value) < 1) {
    return value.toFixed(2);
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(2);
}

function formatPercent(percent: number): string {
  return Number.isInteger(percent) ? `${percent}` : percent.toFixed(1);
}

function buildMetaLine(
  item: { value?: number; percent?: number },
  isYield: boolean
): string | null {
  const parts: string[] = [];

  if (item.value != null && !Number.isNaN(item.value)) {
    const formatted = formatMetricValue(item.value);
    parts.push(isYield ? `${formatted} qx/ha` : formatted);
  }

  if (item.percent != null && !Number.isNaN(item.percent)) {
    parts.push(`${formatPercent(item.percent)} %`);
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

export function AnalysisLegend(props: AnalysisLegendProps) {
  const { t } = useTranslation(['map'], { useSuspense: false });
  const isYield = isYieldZoning(props.zoningType);

  if (!props.legend.length) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles['legend__title']}>
        {isYield ? t('map:yield_legend_title') : t('map:zoning_legend_title')}
      </div>
      <ul className={styles.list}>
        {props.legend.map((item) => {
          const meta = buildMetaLine(item, isYield);

          return (
            <li key={item.zone} className={styles['legend-item']}>
              <span
                className={styles.swatch}
                style={{ backgroundColor: item.color }}
              />
              <div className={styles['legend-text']}>
                <span className={styles['zone-label']}>
                  {t('map:legend_zone', { zone: item.zone })}
                </span>
                {meta ? <span className={styles.meta}>{meta}</span> : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default AnalysisLegend;
